import React, { useState, useMemo } from "react";
import Layout from "@/Components/layout/Layout";
import { BUSINESS_SUBTYPES } from "@/constants";
import { Link } from "react-router-dom"; // Assuming react-router-dom Link
import { FiChevronUp, FiChevronDown, FiSearch } from "react-icons/fi"; // Import search icon

const BusinessMarketPlace = () => {
  // Initialize openCategories with all category names to keep them open by default
  const [openCategories, setOpenCategories] = useState(
    BUSINESS_SUBTYPES.map((group) => group.category)
  );
  // State for the search query
  const [searchQuery, setSearchQuery] = useState("");

  const toggleCategory = (categoryName) => {
    setOpenCategories((prevOpenCategories) => {
      if (prevOpenCategories.includes(categoryName)) {
        return prevOpenCategories.filter((name) => name !== categoryName);
      } else {
        return [...prevOpenCategories, categoryName];
      }
    });
  };

  // Memoize the filtering logic for performance
  const filteredBusinessSubtypes = useMemo(() => {
    if (!searchQuery) {
      // If no search query, return all categories and their original subcategories
      return BUSINESS_SUBTYPES;
    }

    const lowerCaseQuery = searchQuery.toLowerCase();

    return BUSINESS_SUBTYPES.map((group) => {
      // Filter subcategories within each group
      const filteredSubcategories = group.subcategories.filter(
        (sub) =>
          sub.label.toLowerCase().includes(lowerCaseQuery) ||
          sub.description.toLowerCase().includes(lowerCaseQuery) // Optional: search by description too
      );

      // If category title itself matches OR any subcategory matches, include the category
      if (
        group.category.toLowerCase().includes(lowerCaseQuery) ||
        filteredSubcategories.length > 0
      ) {
        return {
          ...group,
          // If category title matches, show all its original subcategories (or you can show only filtered ones)
          // For now, if category title matches, we include all original subcategories for that group.
          // If only subcategories matched, we use the filteredSubcategories.
          subcategories: group.category.toLowerCase().includes(lowerCaseQuery)
            ? group.subcategories // If category name matches, show all its original subcategories
            : filteredSubcategories, // Otherwise, show only the filtered subcategories
        };
      }
      return null; // Exclude category if no match
    }).filter(Boolean); // Remove null entries
  }, [searchQuery]); // Re-run filtering when searchQuery changes

  return (
    <Layout>
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        {" "}
        {/* Reduced from py-16 */}
        <div className="mx-auto">
          {/* Page Title and Description */}
          <div className="text-center mb-6">
            {" "}
            {/* Reduced from mb-8 */}
            <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight leading-tight">
              Aviation Business Directory
            </h1>
            <p className="mt-3 text-lg text-gray-600 !text-center mx-auto ">
              {" "}
              {/* Reduced from mt-4, adjusted text size for compactness */}
              Navigate the skies of opportunity. Discover specialized businesses
              across the aviation sector.
            </p>
          </div>

          {/* Search Bar */}
          <div className="mb-6 max-w-lg mx-auto relative">
            {" "}
            {/* Reduced from mb-8 */}
            <input
              type="text"
              placeholder="Search business categories or types..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 text-base"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="text-gray-400" />
            </div>
          </div>

          {/* Business Categories Grid (Two Accordions per Row) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
            {" "}
            {/* Reduced from gap-4 lg:gap-6 */}
            {filteredBusinessSubtypes.length > 0 ? (
              filteredBusinessSubtypes.map((group) => {
                const isOpen = openCategories.includes(group.category);

                return (
                  <div
                    key={group.category}
                    className=" rounded-xl shadow-lg overflow-hidden"
                  >
                    {/* Category Header (Clickable) */}
                    <button
                      className="w-full bg-white flex items-center justify-between p-4 cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 transition-colors duration-200 hover:bg-gray-50" // Reduced from p-6
                      onClick={() => toggleCategory(group.category)}
                      aria-expanded={isOpen}
                    >
                      <h3 className="text-xl font-semibold text-gray-800">
                        {" "}
                        {/* Reduced text size for compactness */}
                        {group.category}
                      </h3>
                      <i
                        className={`fa-solid fa-chevron-${
                          isOpen ? "up" : "down"
                        } text-red-500 transition-transform duration-300 transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      ></i>
                    </button>

                    {/* Subcategories Content (Collapsible) */}
                    <div
                      className={`grid bg-white grid-cols-1 sm:grid-cols-2  gap-x-6 p-4 transition-all duration-500 ease-in-out border-t border-gray-200 ${
                        // Reduced from gap-y-6 gap-x-8 and p-6
                        isOpen
                          ? " opacity-100 visible"
                          : "max-h-0 opacity-0 invisible !p-0"
                      }`}
                      style={{
                        overflow: isOpen ? "visible" : "hidden", // Set overflow based on isOpen
                      }}
                    >
                      {/* Render filtered subcategories here */}
                      {group.subcategories.map((sub) => {
                        // If there's a search query, only show subcategories that match it,
                        // unless the parent category itself was the primary match.
                        const subMatchesQuery =
                          !searchQuery ||
                          sub.label
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          sub.description
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase());

                        if (subMatchesQuery) {
                          return (
                            <Link
                              key={sub.type}
                              to={`/business/${sub.type}`}
                              className="group flex flex-col items-start text-left p-3 rounded-lg transition-colors duration-200 hover:bg-red-50 hover:shadow-md" // Reduced from p-4
                            >
                              <div className="flex items-center mb-1">
                                {" "}
                                {/* Reduced from mb-2 */}
                                <div className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full bg-red-100 text-red-600 group-hover:bg-red-200 group-hover:text-red-700 transition-all duration-200">
                                  {" "}
                                  {/* Reduced from h-10 w-10 */}
                                  <i className={`${sub.icon} text-lg`} />{" "}
                                  {/* Reduced text-xl to text-lg */}
                                </div>
                                <p className="ml-2 text-base font-medium text-gray-900 group-hover:text-red-800 transition-colors duration-200 !mb-0">
                                  {" "}
                                  {/* Reduced from ml-3, text-lg to text-base */}
                                  {sub.label}
                                </p>
                              </div>
                              <p className="text-sm !p-0 !mb-0 text-gray-600 group-hover:text-gray-700 transition-colors duration-200 leading-snug">
                                {" "}
                                {/* Adjusted leading */}
                                {sub.description}
                              </p>
                            </Link>
                          );
                        }
                        return null; // Don't render if it doesn't match the query
                      })}
                    </div>
                  </div>
                );
              })
            ) : (
              // No Results Found Message
              <div className="md:col-span-2 text-center py-8">
                {" "}
                {/* Reduced from py-10 */}
                <p className="text-lg text-gray-500">
                  {" "}
                  {/* Reduced text-xl to text-lg */}
                  No business categories or types match your search.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BusinessMarketPlace;
